import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  contractDetailsMsg,
  elisaDevicesServiceMsg,
  idNumberMsg,
  imeiSlashSerialNumberMsg,
  monthMsg,
  priceMsg,
  serviceMsg,
  t,
} from '../../common/i18n';
import { formatSum, getRecurringPriceAsText } from '../../common/utils/priceUtils';
import { getPriceWithTax, getVatAsString } from '../../common/utils/taxUtils';
import { getCategoryBySubscriptionType } from '../../public/common/util/category';
import { DetailsWithInfoColumn } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { getDeviceCommitmentFields, getRedeemPrices } from '../EoeSubscriptionDetails/eppSubscriptionUtils';
import { Loading } from '../Loading';
import { getSubscriptionUrl } from '../SubscriptionLayout/MobileSubscriptionLayout';
import { CardContent } from './Components/CardContent';
import {
  DeviceChangeRequestDisclaimer,
  DeviceScheduledChangeDisclaimer,
} from './Components/DeviceChangeRequestDisclaimer';
import { SubscriptionDetailsLinkBlocks, SubscriptionDetailsLinkType } from './SubscriptionDetailsLinkBlocks';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionDetailsDevice } from '../../generated/api/subscriptionDetailsDevice';
import type { State } from '../../selfservice/common/store';

interface InfoColumnProps {
  subscription: Subscription;
}

interface BasicDetailsProps {
  subscription: Subscription;
  companyInfo?: CompanyInfoResponse;
  linkTypes: SubscriptionDetailsLinkType[];
  hasPendingActions: boolean;
  deviceDetails: SubscriptionDetailsDevice;
}

const BasicDetails = ({
  subscription,
  companyInfo,
  linkTypes,
  hasPendingActions,
  deviceDetails,
}: BasicDetailsProps) => {
  const { isTerminateSubscriptionForm, showSuspendSubscriptionOptions } = useSelector((state: State) => ({
    isTerminateSubscriptionForm:
      state?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated ?? false,
    showSuspendSubscriptionOptions:
      state?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated ?? false,
  }));
  const displayedLinkTypes = !isTerminateSubscriptionForm && !showSuspendSubscriptionOptions ? linkTypes : [];

  return (
    <CL.Grid>
      <CL.GridRow>
        <CL.GridCol className={dsClass.PADDING_LEFT_0} colsS={6} colsM={6} colsL={6}>
          <CL.Description
            className={dsClass.MARGIN_0}
            columns={1}
            items={[
              { title: t.OE45(imeiSlashSerialNumberMsg), description: deviceDetails.serialNumber },
              { title: t.YTH3(idNumberMsg), description: subscription.subscriptionDisplayId },
            ]}
          />
        </CL.GridCol>
        <CL.GridCol className={dsClass.PADDING_RIGHT_0} colsS={6} colsM={6} colsL={6}>
          <SubscriptionDetailsLinkBlocks
            linkTypes={displayedLinkTypes}
            companyInfo={companyInfo}
            subscription={subscription}
            hasPendingActions={hasPendingActions}
            category={getCategoryBySubscriptionType(subscription.subscriptionType)}
          />
        </CL.GridCol>
      </CL.GridRow>
    </CL.Grid>
  );
};

const InfoColumn = ({ subscription }: InfoColumnProps) => (
  <CL.Card
    content={<CardContent subscription={subscription} />}
    type="dark"
    analytics-parent-element="OmaElisa instruction card"
  />
);

export interface ContractDetailsProps {
  subscription: Subscription;
  deviceDetails: SubscriptionDetailsDevice;
}

const ContractDetails = ({ subscription, deviceDetails }: ContractDetailsProps) => {
  const getPriceValue = (
    sub: Subscription,
    subscriptionDetailsDevice: SubscriptionDetailsDevice
  ): string | undefined => {
    if (!subscriptionDetailsDevice.omaLaiteLasku) {
      if (sub.details?.contractTerm === 1 && sub.details.oneTimeCharge) {
        return formatSum(sub.details.oneTimeCharge);
      } else if (sub.details?.monthlyRecurringCharge && !sub.details?.oneTimeCharge) {
        return getRecurringPriceAsText(sub.details?.monthlyRecurringCharge);
      } else if (!sub.details?.monthlyRecurringCharge && sub.details?.oneTimeCharge) {
        return formatSum(sub.details?.oneTimeCharge);
      }
      return getRecurringPriceAsText(sub.details?.monthlyRecurringCharge);
    }
    return undefined;
  };

  const items = [
    {
      title: t.CDME(serviceMsg),
      description: deviceDetails.eppSubscription ? t.TRE5(elisaDevicesServiceMsg) : undefined,
    },
    ...getDeviceCommitmentFields(subscription),
    subscription.details?.device?.eppSubscription ? getRedeemPrices(subscription, false) : {},
    {
      title: t.V72N(priceMsg),
      description: getPriceValue(subscription, deviceDetails),
    },
    {
      title: t.G664('Company share'),
      description: deviceDetails.omaLaiteLasku
        ? `${formatSum(deviceDetails.omaLaiteLaskuPrices!.corporateShare)}/${t.XXVX(monthMsg)} (${t.UZ6X('VAT')} 0%)`
        : undefined,
    },
    {
      title: t.LYF6('Employee share'),
      description: deviceDetails.omaLaiteLasku
        ? `${formatSum(getPriceWithTax(deviceDetails.omaLaiteLaskuPrices!.employeeShare ?? 0))}/kk (${t.U8AN(
            'incl.'
          )} ${t.UZ6X('VAT')} ${getVatAsString()}%)`
        : undefined,
    },
  ];

  const fiteredItems = items.filter(item => item.description !== undefined);

  if (fiteredItems.length === 0) {
    return null;
  }

  return (
    <>
      <h3 className={classNames(dsClass.H3_BOOK, dsClass.MARGIN_BOTTOM_7)}>{t.KJ47(contractDetailsMsg)}</h3>
      <CL.Description className={dsClass.MARGIN_0} items={fiteredItems} />
    </>
  );
};

export interface DeviceSubscriptionBasicDetailsProps {
  companyInfo: CompanyInfoResponse;
  subscription: Subscription;
  hasPendingActions: boolean;
  hasScheduledChange: boolean;
  billingAccountRecordType?: string;
}

const getLinkTypes = (isAccessory: boolean, subscription: Subscription, billingAccountRecordType?: string) => {
  if (isAccessory) {
    return [];
  }

  if (subscription.details?.device?.eppSubscription) {
    if (subscription.subscriptionContactId) {
      return [
        SubscriptionDetailsLinkType.EPP_DAMAGE_OR_SUPPORT_REQUEST,
        SubscriptionDetailsLinkType.EPP_REDEEM,
        SubscriptionDetailsLinkType.EPP_DEVICE_CHANGE,
        SubscriptionDetailsLinkType.EPP_TERMINATE,
      ];
    }
    // Device change is not supported for subscriptions that have purpose of use
    return [
      SubscriptionDetailsLinkType.EPP_DAMAGE_OR_SUPPORT_REQUEST,
      SubscriptionDetailsLinkType.EPP_REDEEM,
      SubscriptionDetailsLinkType.EPP_TERMINATE,
    ];
  }

  // Warranty receipts only exist in SF for card payment device subscriptions
  return billingAccountRecordType === 'CardPayment'
    ? [SubscriptionDetailsLinkType.SUPPORT_REQUEST, SubscriptionDetailsLinkType.DOWNLOAD_WARRANTY_RECEIPT]
    : [SubscriptionDetailsLinkType.SUPPORT_REQUEST];
};

export const DeviceSubscriptionBasicDetails = ({
  companyInfo,
  subscription,
  hasPendingActions,
  hasScheduledChange,
  billingAccountRecordType,
}: DeviceSubscriptionBasicDetailsProps) => {
  const isAccessory = subscription?.subscriptionSubType?.toLowerCase() === 'accessories';
  const linkTypes = getLinkTypes(isAccessory, subscription, billingAccountRecordType);
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const scheduledChangeUrl =
    hasScheduledChange && mdmId
      ? getSubscriptionUrl(paths.PS_DEVICE_SUBSCRIPTION_BA_CONTRACT_PARTY, subscription, mdmId)
      : undefined;

  const getDisclaimerContent = () => {
    if (hasScheduledChange) {
      return <DeviceScheduledChangeDisclaimer scheduledChangeUrl={scheduledChangeUrl} />;
    }
    if (hasPendingActions) {
      return <DeviceChangeRequestDisclaimer />;
    }
    return undefined;
  };

  const deviceDetails = subscription.details?.device;

  if (!deviceDetails) {
    return <Loading />;
  }

  return (
    <DetailsWithInfoColumn
      infoColumnContent={<InfoColumn subscription={subscription} />}
      disclaimerContent={getDisclaimerContent()}
    >
      <BasicDetails
        subscription={subscription}
        companyInfo={companyInfo}
        linkTypes={linkTypes}
        hasPendingActions={hasPendingActions || hasScheduledChange}
        deviceDetails={deviceDetails}
      />
      <div className="section-separator" />
      <ContractDetails subscription={subscription} deviceDetails={deviceDetails} />
    </DetailsWithInfoColumn>
  );
};
